import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import useSharedStyles from './theme/pbdqShared.styles';
import { sdsColors, sdsRadius, sdsBtnBase } from './theme/solutionsDesignSystem';
import { pbdqFont } from './theme/pbdqTokens';
import pbdqRoutes from './pbdqRoutes';

import PbdqNav from './components/PbdqNav.comp';
import PbdqFooter from './components/PbdqFooter.comp';
import PbdqImageLightboxWrapper from './components/PbdqImageLightboxWrapper.comp';
import { useAgentChat } from './components/PbdqAgentChatContext';

import LearnCreatorHero from '../../assets/images/PoweredByDQ/LearnCreatorHero.png';
import LearnCreatorHowItWorks1 from '../../assets/images/PoweredByDQ/LearnCreatorHowItWorks1.png';
import LearnCreatorHowItWorks2 from '../../assets/images/PoweredByDQ/LearnCreatorHowItWorks2.png';
import LearnCreatorHowItWorks3 from '../../assets/images/PoweredByDQ/LearnCreatorHowItWorks3.png';
import LearnCreatorHowItWorks4 from '../../assets/images/PoweredByDQ/LearnCreatorHowItWorks4.png';
import LearnCreatorHowItWorks5 from '../../assets/images/PoweredByDQ/LearnCreatorHowItWorks5.png';
import LearnCreatorProgramOutline from '../../assets/images/PoweredByDQ/LearnCreatorProgramOutline.png';
import LearnCreatorProgramResources from '../../assets/images/PoweredByDQ/LearnCreatorProgramResources.png';
import LearnCreatorImpactAssess from '../../assets/images/PoweredByDQ/LearnCreatorImpactAssess.png';
import LearnCreatorAlignment from '../../assets/images/PoweredByDQ/LearnCreatorAlignment.png';
import LearnCreatorSchoolwide from '../../assets/images/PoweredByDQ/LearnCreatorSchoolwide.jpg';

import IconLearners from '../../assets/images/PoweredByDQ/IconLearners.png';
import IconStandardsFrameworks from '../../assets/images/PoweredByDQ/IconStandardsFrameworks.png';
import IconSubjectsTimetable from '../../assets/images/PoweredByDQ/IconSubjectsTimetable.png';
import IconLearningOutcomes from '../../assets/images/PoweredByDQ/IconLearningOutcomes.png';
import IconCultureLocalContext from '../../assets/images/PoweredByDQ/IconCultureLocalContext.png';
import IconDeliveryFormat from '../../assets/images/PoweredByDQ/IconDeliveryFormat.png';

/*
 * LearnCreator — rebuilt to the "LearnCreator page (design system)" mockup.
 * As with COS Assessments and School COS Readiness, the mockup only designs
 * the shell of most sections (headings plus [PLACEHOLDER] lines) — every
 * real paragraph, list and footnote already written for this page is kept
 * and restyled rather than replaced. The final CTA again uses the
 * ctaBand/ctaCard rounded-card pattern instead of the mockup's full-bleed
 * band, per the standing instruction from the COS Assessments page.
 */

const MOBILE = '@media (max-width: 900px)';
const NARROW = '@media (max-width: 1340px)';

const useStyles = makeStyles(() => ({
    accent: { color: sdsColors.accent },
    gutter: {
        maxWidth: '1320px !important',
        margin: '0 auto',
        padding: '0 clamp(20px, 4vw, 56px) !important',
        boxSizing: 'border-box',
    },

    // ── SECTION 0: HERO ────────────────────────────────────────────────────────
    heroSection: {
        background: 'radial-gradient(70% 90% at 86% 10%, #DCE7F3 0%, rgba(220, 231, 243, 0) 60%), linear-gradient(180deg, #F6F8FB 0%, #FFFFFF 100%)',
        overflow: 'hidden',
    },
    heroContainer: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 64px) clamp(20px, 4vw, 56px) clamp(40px, 5vw, 64px)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '28px clamp(32px, 5vw, 80px)',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
    },
    heroTextCol: {
        flex: '1 1 440px',
        minWidth: 0,
        display: 'grid',
        gap: 20,
        alignContent: 'start',
    },
    heroTitle: {
        fontFamily: pbdqFont,
        fontSize: 'clamp(36px, 4.2vw, 60px)',
        lineHeight: 1.04,
        letterSpacing: '-0.03em',
        fontWeight: 800,
        margin: 0,
        maxWidth: '16ch',
        color: '#1B2430',
    },
    heroLead: {
        fontFamily: `${pbdqFont} !important`,
        fontSize: 'clamp(16px, 1.3vw, 18px)',
        lineHeight: 1.55,
        color: '#5B6470',
        margin: 0,
        maxWidth: '58ch',
    },
    heroSubLead: {
        fontFamily: `${pbdqFont} !important`,
        fontSize: 'clamp(16px, 1.3vw, 18px)',
        lineHeight: 1.55,
        color: '#5B6470',
        margin: 0,
        maxWidth: '58ch',
    },
    heroCtas: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        alignItems: 'center',
    },
    btnPrimary: {
        fontFamily: pbdqFont,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: '#ED6628',
        color: '#FFFFFF',
        textDecoration: 'none',
        fontSize: 15,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        border: 0,
        cursor: 'pointer',
        transition: 'background 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            background: '#D5581D',
            color: '#FFFFFF',
            textDecoration: 'none',
        },
    },
    btnSecondary: {
        fontFamily: pbdqFont,
        display: 'inline-flex',
        alignItems: 'center',
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: '#FFFFFF',
        color: '#1B2430',
        border: '1px solid #D5D9E0',
        textDecoration: 'none',
        fontSize: 15,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        transition: 'background 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            background: '#F7F9FB',
            color: '#1B2430',
            textDecoration: 'none',
        },
    },
    heroArtCol: {
        flex: '1 1 400px',
        minWidth: 0,
        maxWidth: 640,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        [MOBILE]: {
            maxWidth: '100%',
            paddingBottom: 0,
        },
    },
    heroImg: {
        width: '100%',
        height: 'auto',
        display: 'block',
    },

    // ── SECTION: PROOF / STATS ────────────────────────────────────────────────
    proofSection: {
        background: '#FFFFFF',
    },
    proofContainer: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(24px, 3vw, 40px) clamp(20px, 4vw, 56px) clamp(32px, 4vw, 56px)',
    },
    proofGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
        gap: 1,
        background: '#E6E9EE',
        border: '1px solid #E6E9EE',
        borderRadius: 24,
        overflow: 'hidden',
    },
    proofItem: {
        background: '#FFFFFF',
        padding: 'clamp(20px, 2.6vw, 32px)',
        display: 'grid',
        gap: 8,
    },
    proofValue: {
        fontFamily: pbdqFont,
        fontSize: 'clamp(34px, 3.6vw, 52px)',
        lineHeight: 1,
        fontWeight: 800,
        letterSpacing: '-0.035em',
        color: '#ED6628',
        margin: 0,
        fontVariantNumeric: 'tabular-nums',
    },
    proofLabel: {
        fontFamily: pbdqFont,
        fontSize: 14,
        lineHeight: 1.45,
        color: '#5B6470',
        margin: 0,
    },

    // Shared section shell
    band: {
        background: sdsColors.surface,
        borderTop: `1px solid ${sdsColors.border}`,
        borderBottom: `1px solid ${sdsColors.border}`,
    },
    bandTint: { background: sdsColors.tint },
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: 40,
        padding: '56px 0',
        [MOBILE]: { padding: '56px 0', gap: 28 },
    },
    sectionHead: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        textAlign: 'left',
    },
    sectionEyebrow: {
        display: 'block',
        fontFamily: pbdqFont,
        fontSize: 13,
        lineHeight: 1,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#2A4057',
        marginBottom: 16,
    },
    sectionTitle: {
        fontFamily: pbdqFont,
        margin: 0,
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        fontWeight: 800,
        lineHeight: 1.08,
        letterSpacing: '-0.025em',
        color: '#1B2430',
        textAlign: 'left',
    },
    sectionIntro: {
        fontFamily: pbdqFont,
        margin: '16px 0 0',
        maxWidth: '72ch',
        fontSize: 15.5,
        lineHeight: 1.6,
        color: '#5B6470',
        textAlign: 'left',
    },
    sectionChip: {
        display: 'block',
        fontFamily: pbdqFont,
        fontSize: 13,
        lineHeight: 1,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#2A4057',
        marginBottom: 16,
    },
    sectionChipSurface: {},
    h2: {
        fontFamily: pbdqFont,
        margin: 0,
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        fontWeight: 800,
        lineHeight: 1.08,
        letterSpacing: '-0.025em',
        color: '#1B2430',
        textAlign: 'left',
    },
    lead: {
        fontFamily: pbdqFont,
        margin: '16px 0 0',
        maxWidth: '72ch',
        fontSize: 15.5,
        lineHeight: 1.6,
        color: '#5B6470',
        textAlign: 'left',
    },

    // ── SECTION: TRY IT ────────────────────────────────────────────────────────
    bandGrey: {
        background: '#F4F6F9',
    },
    tryContainer: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
    },
    tryCard: {
        display: 'flex',
        flexWrap: 'wrap',
        border: '1px solid #E6E9EE',
        borderRadius: 24,
        overflow: 'hidden',
        background: '#FFFFFF',
    },
    tryFormCol: {
        flex: '1 1 400px',
        minWidth: 0,
        padding: 'clamp(24px, 3.2vw, 44px)',
        display: 'grid',
        gap: 18,
        alignContent: 'start',
    },
    tryEyebrow: {
        display: 'block',
        fontFamily: pbdqFont,
        fontSize: 13,
        lineHeight: 1,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#2A4057',
        margin: 0,
    },
    tryTitle: {
        fontFamily: pbdqFont,
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
    },
    tryIntro: {
        fontFamily: pbdqFont,
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#5B6470',
    },
    tryLabel: {
        fontFamily: pbdqFont,
        fontSize: 13,
        fontWeight: 700,
        marginTop: 6,
        color: '#1B2430',
    },
    tryChipsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
    },
    tryChip: {
        fontFamily: pbdqFont,
        padding: '9px 14px',
        borderRadius: 999,
        border: '1px solid #D5D9E0',
        background: '#FFFFFF',
        color: '#1B2430',
        fontSize: 13.5,
        fontWeight: 600,
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'all 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            borderColor: '#1B2430',
        },
    },
    tryChipActive: {
        background: '#1B2430 !important',
        color: '#FFFFFF !important',
        borderColor: '#1B2430 !important',
    },
    tryTextarea: {
        width: '100%',
        padding: '14px 16px',
        border: '1px solid #D5D9E0',
        borderRadius: 14,
        fontFamily: pbdqFont,
        fontSize: 15,
        lineHeight: 1.5,
        color: '#1B2430',
        resize: 'vertical',
        background: '#FFFFFF',
        boxSizing: 'border-box',
        outline: 'none',
        '&:focus': {
            borderColor: sdsColors.accent,
        },
    },
    trySubmitBtn: {
        justifySelf: 'start',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        height: 50,
        padding: '0 24px',
        borderRadius: 999,
        border: 0,
        background: '#ED6628',
        color: '#FFFFFF',
        fontFamily: pbdqFont,
        fontSize: 15,
        fontWeight: 700,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'background 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            background: '#D5581D',
        },
    },
    tryFootnote: {
        fontFamily: pbdqFont,
        fontSize: 13,
        lineHeight: 1.55,
        margin: 0,
        color: '#6B7280',
    },
    tryPreviewCol: {
        flex: '1 1 380px',
        minWidth: 0,
        padding: 'clamp(24px, 3.2vw, 44px)',
        background: '#172939',
        color: '#FFFFFF',
        display: 'grid',
        alignContent: 'center',
    },
    tryEmptyWrap: {
        display: 'grid',
        gap: 12,
        justifyItems: 'center',
        textAlign: 'center',
        padding: '24px 0',
    },
    trySparkIcon: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 56,
        height: 56,
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.08)',
        fontSize: 22,
        color: '#FFFFFF',
    },
    tryEmptyText: {
        fontFamily: pbdqFont,
        fontSize: 16,
        lineHeight: 1.5,
        margin: 0,
        color: '#B7C8DA',
        maxWidth: '34ch',
    },
    tryGeneratedWrap: {
        display: 'grid',
        gap: 16,
    },
    tryGeneratedHead: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 10,
    },
    tryGeneratedTitle: {
        fontFamily: pbdqFont,
        fontSize: 'clamp(20px, 1.9vw, 24px)',
        fontWeight: 800,
        letterSpacing: '-0.02em',
        color: '#FFFFFF',
        margin: 0,
    },
    tryDraftBadge: {
        fontFamily: pbdqFont,
        padding: '5px 10px',
        borderRadius: 999,
        background: '#ED6628',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        color: '#FFFFFF',
    },
    tryMetaSubtitle: {
        fontFamily: pbdqFont,
        fontSize: 13.5,
        margin: 0,
        color: '#B7C8DA',
    },
    trySessionList: {
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'grid',
        gap: 8,
    },
    trySessionItem: {
        display: 'grid',
        gridTemplateColumns: '36px 1fr auto',
        gap: 12,
        alignItems: 'center',
        padding: '12px 14px',
        borderRadius: 12,
        background: 'rgba(255, 255, 255, 0.07)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
    },
    trySessionNo: {
        fontFamily: pbdqFont,
        fontSize: 12,
        fontWeight: 700,
        color: '#8AA3BC',
        fontVariantNumeric: 'tabular-nums',
    },
    trySessionTitle: {
        fontFamily: pbdqFont,
        fontSize: 14.5,
        fontWeight: 600,
        color: '#FFFFFF',
    },
    trySessionMeta: {
        fontFamily: pbdqFont,
        fontSize: 12,
        color: '#B7C8DA',
        textAlign: 'right',
    },

    // Interactive 5-step "how it works" stepper
    stepperRow: {
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
        gap: 16,
    },
    stepperLine: {
        position: 'absolute',
        left: '10%',
        right: '10%',
        top: 36,
        height: 3,
        background: sdsColors.border,
    },
    stepperBtn: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '0 8px 16px',
        border: 0,
        background: 'transparent',
        textAlign: 'center',
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
    stepperCircle: {
        position: 'relative',
        zIndex: 1,
        width: 72,
        height: 72,
        borderRadius: '50%',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 22,
        fontWeight: 700,
        color: sdsColors.text,
    },
    stepperKey: {
        fontFamily: pbdqFont,
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: '0.09em',
        textTransform: 'uppercase',
    },
    stepperTitle: {
        fontFamily: pbdqFont,
        fontSize: 16,
        fontWeight: 700,
        lineHeight: 1.3,
        color: sdsColors.text,
    },
    stepperBar: {
        width: 40,
        height: 4,
        borderRadius: sdsRadius.pill,
    },
    stepCard: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.15fr)',
        background: sdsColors.surface,
        border: `1px solid ${sdsColors.border}`,
        borderRadius: 20,
        overflow: 'hidden',
        boxShadow: '0 24px 48px -32px rgba(15,42,68,0.35)',
        [MOBILE]: { gridTemplateColumns: '1fr' },
    },
    stepCardText: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: 48,
        [MOBILE]: { padding: 28 },
    },
    stepCardRail: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        height: 6,
    },
    stepCardPill: {
        fontFamily: `${pbdqFont} !important`,
        alignSelf: 'flex-start',
        padding: '6px 12px',
        borderRadius: sdsRadius.pill,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: sdsColors.text,
    },
    stepCardTitle: {
        fontFamily: `${pbdqFont} !important`,
        margin: 0,
        fontSize: 'clamp(24px, 2.6vw, 34px)',
        fontWeight: 800,
        lineHeight: 1.12,
        letterSpacing: '-0.02em',
        color: '#1B2430',
    },
    stepCardCopy: {
        fontFamily: `${pbdqFont} !important`,
        margin: 0,
        fontSize: 15.5,
        lineHeight: 1.65,
        color: '#5B6470',
    },
    stepCardOutputBox: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: '16px 18px',
        borderRadius: 14,
        background: sdsColors.bg,
        transition: 'background 240ms ease, border-color 240ms ease',
    },
    stepCardOutputLabel: {
        fontFamily: `${pbdqFont} !important`,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: sdsColors.textSubtle,
    },
    stepCardOutputText: {
        fontFamily: `${pbdqFont} !important`,
        fontSize: 14.5,
        lineHeight: 1.5,
        color: '#1B2430',
    },
    stepCardNav: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 'auto',
        paddingTop: 8,
    },
    stepNavBtn: {
        fontFamily: pbdqFont,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 44,
        padding: '0 20px',
        borderRadius: 999,
        border: `1px solid ${sdsColors.border}`,
        background: sdsColors.surface,
        fontSize: 14,
        fontWeight: 600,
        color: sdsColors.textMuted,
        cursor: 'pointer',
        transition: 'all 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            borderColor: sdsColors.accent,
            color: sdsColors.text,
        },
    },
    stepNavBtnPrimary: {
        border: 'none',
        background: sdsColors.accent,
        color: '#fff',
        fontWeight: 700,
        '&:hover': {
            background: sdsColors.accentDark,
            color: '#fff',
        },
    },
    stepCardImageWrap: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40,
        [MOBILE]: { padding: 20 },
    },
    stepCardLightboxWrap: {
        display: 'block',
        width: '100%',
        height: 360,
        borderRadius: 14,
        [MOBILE]: { height: 240 },
    },
    stepCardImage: {
        display: 'block',
        width: '100%',
        height: 360,
        objectFit: 'cover',
        objectPosition: 'top',
        borderRadius: 14,
        border: '6px solid #fff',
        boxShadow: '0 20px 40px -24px rgba(15,42,68,0.4)',
        [MOBILE]: { height: 240 },
    },

    // Reusable rail-card grid (build / customize / why)
    grid3: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 24,
        [MOBILE]: { gridTemplateColumns: '1fr' },
    },
    railCard: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        background: sdsColors.surface,
        border: `1px solid ${sdsColors.border}`,
        borderRadius: 16,
        overflow: 'hidden',
    },
    railCardRail: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        height: 4,
    },
    railCardLightboxWrap: {
        display: 'block',
        width: '100%',
        height: 200,
        borderRadius: 0,
    },
    railCardImage: {
        display: 'block',
        width: '100%',
        height: 200,
        objectFit: 'cover',
    },
    railCardBody: {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: 28,
    },
    railCardIndex: {
        fontFamily: `${pbdqFont} !important`,
        fontSize: 13,
        fontWeight: 800,
        fontVariantNumeric: 'tabular-nums',
    },
    railCardTitle: {
        fontFamily: `${pbdqFont} !important`,
        margin: 0,
        fontSize: 20,
        fontWeight: 800,
        lineHeight: 1.2,
        letterSpacing: '-0.015em',
        color: '#1B2430',
    },
    customiseHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    customiseIcon: {
        width: 32,
        height: 32,
        objectFit: 'contain',
        flexShrink: 0,
    },
    railCardDesc: {
        fontFamily: `${pbdqFont} !important`,
        margin: 0,
        fontSize: 14.5,
        lineHeight: 1.6,
        color: '#5B6470',
    },

    // Included-in-deployment box
    includedBox: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: 32,
        borderRadius: 16,
        background: sdsColors.navy,
    },
    includedTitle: {
        fontFamily: pbdqFont,
        margin: 0,
        fontSize: 18,
        fontWeight: 700,
        color: '#fff',
    },
    includedGrid: {
        margin: 0,
        padding: 0,
        listStyle: 'none',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: '10px 32px',
        [MOBILE]: { gridTemplateColumns: '1fr' },
    },
    includedItem: {
        fontFamily: pbdqFont,
        display: 'flex',
        gap: 10,
        fontSize: 14,
        lineHeight: 1.5,
        color: '#E6ECF2',
    },
    checkIcon: { flexShrink: 0, marginTop: 2 },
    includedFootnote: {
        fontFamily: pbdqFont,
        margin: 0,
        fontSize: 13.5,
        lineHeight: 1.55,
        color: '#9FB3C8',
    },

    // Two-ways cards
    twoWaysGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 24,
        [MOBILE]: { gridTemplateColumns: '1fr' },
    },
    twoWaysCard: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: 36,
        borderRadius: 16,
        background: sdsColors.surface,
        border: `1px solid ${sdsColors.border}`,
        overflow: 'hidden',
    },
    twoWaysRail: {
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 5,
    },
    twoWaysTitle: {
        fontFamily: `${pbdqFont} !important`,
        margin: 0,
        fontSize: 24,
        fontWeight: 800,
        color: sdsColors.text,
    },
    twoWaysCopy: {
        fontFamily: `${pbdqFont} !important`,
        margin: 0,
        fontSize: 16,
        lineHeight: 1.6,
        color: sdsColors.textMuted,
    },
    twoWaysNote: {
        fontFamily: pbdqFont,
        margin: 0,
        alignSelf: 'center',
        maxWidth: 760,
        textAlign: 'center',
        fontSize: 17,
        fontWeight: 600,
        lineHeight: 1.5,
        color: sdsColors.accentDark,
    },

    // Two-column (text + image) sections
    twoColGrid: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.2fr)',
        gap: 56,
        alignItems: 'center',
        [MOBILE]: { gridTemplateColumns: '1fr', gap: 32 },
    },
    twoColGridEven: {
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        [MOBILE]: { gridTemplateColumns: '1fr' },
    },
    twoColCopy: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        textAlign: 'left',
    },
    twoColPara: {
        margin: 0,
        fontSize: 17,
        lineHeight: 1.6,
        color: sdsColors.textMuted,
    },
    checkList: {
        margin: 0,
        padding: 0,
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
    },
    checkItem: {
        display: 'flex',
        gap: 10,
        fontSize: 16,
        lineHeight: 1.5,
        color: sdsColors.text,
    },
    twoColLightboxWrap: {
        display: 'block',
        width: '100%',
        height: 420,
        borderRadius: 16,
        [MOBILE]: { height: 280 },
    },
    twoColImage: {
        display: 'block',
        width: '100%',
        height: 420,
        objectFit: 'cover',
        objectPosition: 'top',
        borderRadius: 16,
        border: '6px solid #fff',
        boxShadow: '0 24px 48px -28px rgba(15,42,68,0.4)',
        [MOBILE]: { height: 280 },
    },
    footnote: {
        margin: 0,
        fontSize: 13.5,
        lineHeight: 1.55,
        color: sdsColors.textSubtle,
    },

    // Credits example card
    exampleCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        padding: 32,
        borderRadius: 16,
        background: sdsColors.surface,
        border: '2px solid #F8B143',
    },
    exampleLabel: {
        fontFamily: pbdqFont,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.09em',
        textTransform: 'uppercase',
        color: '#C98410',
    },
    exampleIntro: {
        fontFamily: pbdqFont,
        margin: 0,
        fontSize: 17,
        fontWeight: 700,
        lineHeight: 1.45,
        color: sdsColors.text,
    },

    // Standards card
    standardsCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '20px 24px',
        borderRadius: 12,
        background: sdsColors.navy,
    },
    standardsCardLabel: {
        fontFamily: pbdqFont,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.09em',
        textTransform: 'uppercase',
        color: '#9FB3C8',
    },
    standardsCardText: {
        fontFamily: pbdqFont,
        margin: 0,
        fontSize: 15,
        lineHeight: 1.55,
        color: '#fff',
    },

    // Final CTA
    ctaSection: {
        background: '#172939',
        color: '#FFFFFF',
    },
    ctaContainer: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '24px 48px',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    ctaTextCol: {
        flex: '1 1 420px',
        minWidth: 0,
        display: 'grid',
        gap: 12,
        textAlign: 'left',
    },
    ctaTitle: {
        fontFamily: pbdqFont,
        margin: 0,
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.03em',
        fontWeight: 800,
        color: '#FFFFFF',
        textAlign: 'left',
        maxWidth: '22ch',
    },
    ctaLead: {
        fontFamily: pbdqFont,
        margin: 0,
        fontSize: 15.5,
        lineHeight: 1.6,
        color: '#D9E3EE',
        maxWidth: '60ch',
        textAlign: 'left',
    },
    ctaActions: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
    },
    btnCtaPrimary: {
        fontFamily: pbdqFont,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: '#ED6628',
        color: '#FFFFFF',
        textDecoration: 'none',
        fontSize: 15,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        border: 0,
        cursor: 'pointer',
        transition: 'background 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            background: '#D5581D',
            color: '#FFFFFF',
            textDecoration: 'none',
        },
    },
    btnCtaOutline: {
        fontFamily: pbdqFont,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: 'transparent',
        border: '1px solid #5D7A96',
        color: '#FFFFFF',
        textDecoration: 'none',
        fontSize: 15,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        transition: 'background 200ms cubic-bezier(.32,.72,0,1), border-color 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            background: 'rgba(255, 255, 255, 0.1)',
            borderColor: '#FFFFFF',
            color: '#FFFFFF',
            textDecoration: 'none',
        },
    },
}));

const TRY_PROMPTS = [
    'Help students manage their screen time',
    'Teach students how to respond to cyberbullying',
    'Build students’ cybersecurity habits',
    'Help students recognise misinformation',
    'Teach students how to protect their personal data',
];

const TRY_OUTLINES = {
    'Help students manage their screen time': {
        title: 'Screen time and digital balance',
        sessions: [
            { no: '01', title: 'Session 1 · Where the time goes', meta: '45 min' },
            { no: '02', title: 'Session 2 · Designed to keep you scrolling', meta: '45 min' },
            { no: '03', title: 'Session 3 · Setting your own limits', meta: '45 min' },
            { no: '04', title: 'Pre and post assessment', meta: '10 questions each · rolls up by class and school' },
        ],
    },
    'Teach students how to respond to cyberbullying': {
        title: 'Cyberbullying and bystander action',
        sessions: [
            { no: '01', title: 'Session 1 · What it looks like', meta: '45 min' },
            { no: '02', title: 'Session 2 · The bystander’s choice', meta: '45 min' },
            { no: '03', title: 'Session 3 · Getting help that works', meta: '45 min' },
            { no: '04', title: 'Pre and post assessment', meta: '10 questions each · rolls up by class and school' },
        ],
    },
    'Build students’ cybersecurity habits': {
        title: 'Everyday cybersecurity habits',
        sessions: [
            { no: '01', title: 'Session 1 · Passwords and locks', meta: '45 min' },
            { no: '02', title: 'Session 2 · Spotting the scam', meta: '45 min' },
            { no: '03', title: 'Session 3 · Habits that stick', meta: '45 min' },
            { no: '04', title: 'Pre and post assessment', meta: '10 questions each · rolls up by class and school' },
        ],
    },
    'Help students recognise misinformation': {
        title: 'Misinformation and critical reading',
        sessions: [
            { no: '01', title: 'Session 1 · Why it spreads', meta: '45 min' },
            { no: '02', title: 'Session 2 · Checking before sharing', meta: '45 min' },
            { no: '03', title: 'Session 3 · Reading past the headline', meta: '45 min' },
            { no: '04', title: 'Pre and post assessment', meta: '10 questions each · rolls up by class and school' },
        ],
    },
    'Teach students how to protect their personal data': {
        title: 'Personal data and privacy',
        sessions: [
            { no: '01', title: 'Session 1 · What you give away', meta: '45 min' },
            { no: '02', title: 'Session 2 · Settings that matter', meta: '45 min' },
            { no: '03', title: 'Session 3 · Your data, your call', meta: '45 min' },
            { no: '04', title: 'Pre and post assessment', meta: '10 questions each · rolls up by class and school' },
        ],
    },
};

const getOutlineForPrompt = (prompt) => {
    const p = (prompt || '').trim();
    if (TRY_OUTLINES[p]) return TRY_OUTLINES[p];
    return {
        title: p || 'Draft program',
        sessions: [
            { no: '01', title: 'Session 1 · Naming the problem', meta: '45 min' },
            { no: '02', title: 'Session 2 · Practising the response', meta: '45 min' },
            { no: '03', title: 'Session 3 · Making it a habit', meta: '45 min' },
            { no: '04', title: 'Pre and post assessment', meta: '10 questions each · rolls up by class and school' },
        ],
    };
};

const STEPS = [
    {
        key: 'Define', num: '01', title: 'Tell LearnCreator what your learners need.',
        rail: '#3CC6F3', tint: '#E0F5FD', deep: '#1B9CC9',
        desc: 'Describe the learners, topic, objectives, curriculum context and delivery format. You can begin in plain language or use assessment findings to identify the capabilities that need attention.',
        // output: 'A clear program brief based on your learning need.',
        image: LearnCreatorHowItWorks1, alt: 'Describing a program topic to DQ AI in LearnCreator',
    },
    {
        key: 'Generate', num: '02', title: 'Create a complete first draft.',
        rail: '#B7D334', tint: '#F1F7DC', deep: '#6E8A12',
        desc: 'LearnCreator generates a coherent program with learning objectives, session plans, activities, resources and assessments designed to measure the intended outcomes.',
        output: 'A structured, editable program ready for educator review.',
        image: LearnCreatorHowItWorks2, alt: 'LearnCreator generating a program with DQ AI',
    },
    {
        key: 'Adapt', num: '03', title: 'Make it right for your session.',
        rail: '#F8B143', tint: '#FEF1DC', deep: '#C98410',
        desc: 'Review and refine the content, examples, language, standards alignment, subjects, learning activities and assessment approach. Educators remain responsible for approving the program before it is published.',
        output: 'A customized program adapted to your curriculum and context.',
        image: LearnCreatorHowItWorks3, alt: 'Editing program information in LearnCreator',
    },
    {
        key: 'Publish', num: '04', title: 'Deliver it where learning already happens.',
        rail: '#ED6628', tint: '#FDECE3', deep: sdsColors.accentDark,
        desc: 'Assign the completed program to selected learners or groups of learners through Powered by DQ — or integrate it into your existing classroom workflow.',
        output: 'A published program ready for teaching and learning.',
        image: LearnCreatorHowItWorks4, alt: 'Previewing a completed program in LearnCreator',
    },
    {
        key: 'Track', num: '05', title: 'Measure participation and learning impact.',
        rail: '#C91C6E', tint: '#F8E4EE', deep: '#C91C6E',
        desc: 'Use embedded assessments to monitor completion and determine whether learners developed the intended capabilities. Results flow into dashboards at every level, helping educators identify progress and remaining gaps.',
        output: 'Evidence of learning progress and program impact.',
        image: LearnCreatorHowItWorks5, alt: 'A LearnCreator impact dashboard',
    },
];

const BUILD_ITEMS = [
    { index: '01', deep: '#C98410', rail: '#F8B143', title: 'Program outline', desc: 'A coherent program structure with clear learning objectives and session plans — what learners should know, understand, apply and demonstrate, and how each session delivers it.', image: LearnCreatorProgramOutline, alt: 'A LearnCreator program outline' },
    { index: '02', deep: sdsColors.accentDark, rail: '#ED6628', title: 'Learning programs and resources', desc: 'Structured learning materials including activities, videos, slides, interactive functions and learning resources — age-appropriate and adapted to the program format and context.', image: LearnCreatorProgramResources, alt: 'A LearnCreator lesson resource' },
    { index: '03', deep: '#C91C6E', rail: '#C91C6E', title: 'Impact assessment and dashboard', desc: 'Pre- and post-program assessments aligned with the learning objectives, with results flowing into dashboards at every level — so educators can see what students learned, how capability changed and where further support is needed.', image: LearnCreatorImpactAssess, alt: 'A LearnCreator impact dashboard' },
];

const INCLUDED_ITEMS = [
    'School, network and educator workspaces',
    'Program and session creation tools',
    'Curriculum and framework alignment',
    'Editing and approval controls',
    'Embedded impact-assessment creation',
    'Program publishing and learner delivery',
    'Progress and impact reporting',
    'Unlimited standard educator and creator accounts',
    'Bundled creation capacity',
];

const CUSTOMISE_ITEMS = [
    { title: 'Learners', icon: IconLearners, rail: '#3CC6F3', desc: 'Adapt the program by age, year group, ability, learning need or other relevant student characteristics.' },
    { title: 'Standards and frameworks', icon: IconStandardsFrameworks, rail: '#B7D334', desc: 'Align learning with your national curriculum, institutional standards and selected international frameworks.' },
    { title: 'Subjects and timetable', icon: IconSubjectsTimetable, rail: '#F8B143', desc: 'Deliver the program independently or integrate it into subjects and activities already on the timetable.' },
    { title: 'Learning outcomes', icon: IconLearningOutcomes, rail: '#ED6628', desc: 'Choose the specific knowledge, skills, attitudes, values and behaviors the program should develop.' },
    { title: 'Culture and local context', icon: IconCultureLocalContext, rail: '#C91C6E', desc: 'Adapt the language, examples, scenarios, sensitivities and references to the communities your school serves.' },
    { title: 'Delivery format', icon: IconDeliveryFormat, rail: '#6A8EC9', desc: 'Configure the number of sessions, session length, learning activities and mode of delivery.' },
];

const SCHOOLWIDE_ITEMS = [
    'Give educators access without purchasing separate creator seats',
    'Pool creation capacity across the school or network',
    'Share and adapt programs across teaching teams',
    'Maintain school- and network-level visibility',
];

const EXAMPLE_ITEMS = [
    'Two 10-session programs',
    'Four 5-session programs',
    'A combination of shorter and longer programs',
    'Different programs for selected subjects or year groups',
];

const WHY_ITEMS = [
    { title: 'Faster program development', rail: '#3CC6F3', desc: 'Move from an identified learning need to a structured first draft in minutes, reducing the time required to begin curriculum development.' },
    { title: 'Learning tailored to real needs', rail: '#B7D334', desc: 'Create programs for specific learners, curriculum priorities, capability gaps and local contexts instead of relying only on generic content.' },
    { title: 'Educators remain in control', rail: '#F8B143', desc: 'Teachers and school leaders review, edit and approve every program before it is published or delivered.' },
    { title: 'Assessment is built in', rail: '#ED6628', desc: 'Generate learning and its corresponding impact assessments together, maintaining a clear connection between objectives, activities and measurement.' },
    { title: 'Flexible curriculum integration', rail: '#C91C6E', desc: 'Run programs independently or embed individual sessions into subjects and activities already taught.' },
    { title: 'Scalable across networks', rail: '#6A8EC9', desc: 'Create centrally, distribute consistently and allow approved local adaptation across multiple schools.' },
];

const CheckIcon = ({ color = '#ED6628' }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
);

const PbdqLearnCreatorPageContent = () => {
    const shared = useSharedStyles();
    const classes = useStyles();
    const openAgentChat = useAgentChat();
    const [promptText, setPromptText] = useState('');
    const [isGenerated, setIsGenerated] = useState(false);
    const [step, setStep] = useState(0);
    const cur = STEPS[step];
    const last = STEPS.length - 1;

    const scrollToHowItWorks = () => {
        const el = document.getElementById('how-it-works');
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div className={shared.page} style={{ background: sdsColors.bg, fontFamily: pbdqFont }}>
            <PbdqNav />

            <section aria-label="Introduction" className={classes.heroSection}>
                <div className={classes.heroContainer}>
                    <div className={classes.heroTextCol}>
                        <h1 className={classes.heroTitle}>
                            <span style={{ display: 'block' }}>Create the learning your students</span>
                            <span style={{ display: 'block', color: '#ED6628' }}>need—in minutes.</span>
                        </h1>
                        <p className={classes.heroLead} style={{ fontFamily: pbdqFont }}>
                            Turn a learning need into a structured, assessment-backed program tailored to your students, curriculum and local context.
                        </p>
                        <p className={classes.heroLead} style={{ fontFamily: pbdqFont }}>
                            LearnCreator generates the program outline, lessons, activities and impact assessments together—giving educators a strong first draft to review, customize and deliver. No specialist curriculum-design expertise is required.
                        </p>
                        <div className={classes.heroCtas}>
                            <button
                                type="button"
                                onClick={scrollToHowItWorks}
                                className={classes.btnPrimary}
                            >
                                Try LearnCreator
                            </button>
                            <Link
                                to={pbdqRoutes.getStarted}
                                className={classes.btnSecondary}
                            >
                                Get started
                            </Link>
                        </div>
                    </div>

                    <div className={classes.heroArtCol}>
                        <img
                            src={LearnCreatorHero}
                            alt="LearnCreator platform overview: build AI-digital programs in minutes"
                            className={classes.heroImg}
                        />
                    </div>
                </div>
            </section>

            <section aria-label="Proof" className={classes.proofSection}>
                <div className={classes.proofContainer}>
                    <div className={classes.proofGrid}>
                        <div className={classes.proofItem}>
                            <p className={classes.proofValue}>~100×</p>
                            <p className={classes.proofLabel}>faster than conventional program development</p>
                        </div>
                        <div className={classes.proofItem}>
                            <p className={classes.proofValue}>~1%</p>
                            <p className={classes.proofLabel}>of the conventional development cost</p>
                        </div>
                        <div className={classes.proofItem}>
                            <p className={classes.proofValue}>1,000+</p>
                            <p className={classes.proofLabel}>frameworks and programs referenced</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* <section aria-label="Try it" className={classes.bandGrey}>
                <div className={classes.tryContainer}>
                    <div id="try" className={classes.tryCard}>
                        <div className={classes.tryFormCol}>
                            <span className={classes.tryEyebrow}>Try it</span>
                            <h2 className={classes.tryTitle}>Describe a learning need.</h2>
                            <p className={classes.tryIntro}>
                                Choose an example or describe something you want your students to learn. LearnCreator will generate an illustrative program outline. Nothing in this preview will be saved or published.
                            </p>
                            <label htmlFor="lc-prompt" className={classes.tryLabel}>What would you like to teach?</label>
                            <div className={classes.tryChipsRow}>
                                {TRY_PROMPTS.map((prompt) => {
                                    const active = promptText === prompt;
                                    return (
                                        <button
                                            key={prompt}
                                            type="button"
                                            aria-pressed={active}
                                            onClick={() => {
                                                setPromptText(prompt);
                                                setIsGenerated(false);
                                            }}
                                            className={clsx(classes.tryChip, active && classes.tryChipActive)}
                                        >
                                            {prompt}
                                        </button>
                                    );
                                })}
                            </div>
                            <textarea
                                id="lc-prompt"
                                rows={3}
                                value={promptText}
                                onChange={(e) => {
                                    setPromptText(e.target.value);
                                    setIsGenerated(false);
                                }}
                                placeholder="e.g. Help Year 7 students use AI tools responsibly for homework"
                                className={classes.tryTextarea}
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    if (!promptText.trim()) {
                                        setPromptText(TRY_PROMPTS[0]);
                                    }
                                    setIsGenerated(true);
                                }}
                                className={classes.trySubmitBtn}
                            >
                                ✦ Generate a preview
                            </button>
                            <p className={classes.tryFootnote}>
                                The preview is illustrative. A complete draft is generated using your selected learners, learning objectives, curriculum standards and local context.
                            </p>
                        </div>

                        <div className={classes.tryPreviewCol}>
                            {!isGenerated || !promptText.trim() ? (
                                <div className={classes.tryEmptyWrap}>
                                    <span className={classes.trySparkIcon}>✦</span>
                                    <p className={classes.tryEmptyText}>
                                        Your draft program outline will appear here — sessions, timing and the embedded assessment.
                                    </p>
                                </div>
                            ) : (
                                <div className={classes.tryGeneratedWrap}>
                                    <div className={classes.tryGeneratedHead}>
                                        <h3 className={classes.tryGeneratedTitle}>
                                            {getOutlineForPrompt(promptText).title}
                                        </h3>
                                        <span className={classes.tryDraftBadge}>
                                            Draft preview
                                        </span>
                                    </div>
                                    <p className={classes.tryMetaSubtitle}>
                                        Ages 10–12 · 3 sessions × 45 minutes · assessment embedded
                                    </p>
                                    <ol className={classes.trySessionList}>
                                        {getOutlineForPrompt(promptText).sessions.map((s) => (
                                            <li key={s.no} className={classes.trySessionItem}>
                                                <span className={classes.trySessionNo}>{s.no}</span>
                                                <span className={classes.trySessionTitle}>{s.title}</span>
                                                <span className={classes.trySessionMeta}>{s.meta}</span>
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section> */}

            <div className={classes.bandGrey}>
                <div className={clsx(shared.container, classes.gutter)}>
                <section id="how-it-works" className={classes.section}>
                    <div className={classes.sectionHead}>
                        <span className={classes.sectionEyebrow}>How it works</span>
                        <h2 className={classes.sectionTitle}>From learning need to <span className={classes.accent}>ready-to-deliver program.</span></h2>
                        <p className={classes.sectionIntro}>LearnCreator guides educators through a structured creation process while keeping them in control of the final learning experience.</p>
                    </div>

                    <div className={classes.stepperRow} role="group" aria-label="How LearnCreator works">
                        <span className={classes.stepperLine} />
                        {STEPS.map((s, i) => {
                            const on = i === step;
                            const done = i < step;
                            return (
                                <button key={s.key} type="button" aria-pressed={on} onClick={() => setStep(i)} className={classes.stepperBtn}>
                                    <span className={classes.stepperCircle} style={{ border: `4px solid ${s.rail}`, background: on ? s.rail : done ? s.tint : '#fff' }}>{s.num}</span>
                                    <span className={classes.stepperKey} style={{ color: s.deep }}>{s.key}</span>
                                    <span className={classes.stepperTitle}>{s.title}</span>
                                    <span className={classes.stepperBar} style={{ background: on ? sdsColors.accent : 'transparent' }} />
                                </button>
                            );
                        })}
                    </div>

                    <div className={classes.stepCard}>
                        <div className={classes.stepCardText}>
                            <span className={classes.stepCardRail} style={{ background: cur.rail }} />
                            <span className={classes.stepCardPill} style={{ background: cur.tint, color: cur.deep, fontFamily: pbdqFont }}>Step {cur.num} · {cur.key}</span>
                            <h3 className={classes.stepCardTitle} style={{ fontFamily: pbdqFont }}>{cur.title}</h3>
                            <p className={classes.stepCardCopy} style={{ fontFamily: pbdqFont }}>{cur.desc}</p>
                            {cur.output && (
                                <div className={classes.stepCardOutputBox} style={{ background: cur.tint, border: `1px solid ${cur.rail}40` }}>
                                    <span className={classes.stepCardOutputLabel} style={{ color: cur.deep, fontFamily: pbdqFont }}>Output</span>
                                    <span className={classes.stepCardOutputText} style={{ fontFamily: pbdqFont }}>{cur.output}</span>
                                </div>
                            )}
                            <div className={classes.stepCardNav}>
                                <button type="button" onClick={() => setStep((step + last) % STEPS.length)} className={classes.stepNavBtn}>← Previous</button>
                                <button type="button" onClick={() => setStep((step + 1) % STEPS.length)} className={clsx(classes.stepNavBtn, classes.stepNavBtnPrimary)}>
                                    {step < last ? `Next: ${STEPS[step + 1].key} →` : 'Start again ↺'}
                                </button>
                            </div>
                        </div>
                        <div className={classes.stepCardImageWrap} style={{ background: cur.tint }}>
                            <PbdqImageLightboxWrapper
                                src={cur.image}
                                alt={cur.title}
                                caption={`Step ${cur.num}: ${cur.title}`}
                                className={classes.stepCardLightboxWrap}
                            >
                                <img src={cur.image} alt={cur.alt} className={classes.stepCardImage} />
                            </PbdqImageLightboxWrapper>
                        </div>
                    </div>
                </section>
                </div>
            </div>

            

            <div className={classes.bandTint}>
                <div className={clsx(shared.container, classes.gutter)}>
                    <section className={classes.section}>
                        <div className={classes.sectionHead}>
                            <span className={classes.sectionEyebrow}>What LearnCreator builds</span>
                            <h2 className={classes.sectionTitle}>More than a <span className={classes.accent}>lesson outline.</span></h2>
                            <p className={classes.sectionIntro}>Each program can include the connected components required to move from an idea to measurable learning.</p>
                        </div>
                        <div className={classes.grid3}>
                            {BUILD_ITEMS.map((item) => (
                                <div key={item.title} className={classes.railCard}>
                                    <span className={classes.railCardRail} style={{ background: item.rail }} />
                                    <PbdqImageLightboxWrapper
                                        src={item.image}
                                        alt={item.title}
                                        caption={item.title}
                                        className={classes.railCardLightboxWrap}
                                    >
                                        <img src={item.image} alt={item.alt} className={classes.railCardImage} />
                                    </PbdqImageLightboxWrapper>
                                    <div className={classes.railCardBody}>
                                        <span className={classes.railCardIndex} style={{ color: item.deep, fontFamily: pbdqFont }}>{item.index}</span>
                                        <h3 className={classes.railCardTitle} style={{ fontFamily: pbdqFont }}>{item.title}</h3>
                                        <p className={classes.railCardDesc} style={{ fontFamily: pbdqFont }}>{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className={classes.includedBox}>
                            <h3 className={classes.includedTitle}>Everything above is included in your deployment.</h3>
                            <ul className={classes.includedGrid}>
                                {INCLUDED_ITEMS.map((item) => (
                                    <li key={item} className={classes.includedItem}>
                                        <span className={classes.checkIcon}><CheckIcon color="#F8B143" /></span>
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className={classes.includedFootnote}>There are no separate charges for standard educator accounts, ordinary creator access, core editing tools or standard program delivery within the agreed deployment.</p>
                        </div>
                    </section>
                </div>
            </div>

            <div className={clsx(shared.container, classes.gutter)}>
                <section className={classes.section}>
                    <div className={classes.sectionHead}>
                        <span className={classes.sectionEyebrow}>What you can customize</span>
                        <h2 className={classes.sectionTitle}>Built around your curriculum — <span className={classes.accent}>not imposed on it.</span></h2>
                    </div>
                    <div className={classes.grid3}>
                        {CUSTOMISE_ITEMS.map((item) => (
                            <div key={item.title} className={classes.railCard}>
                                <span className={classes.railCardRail} style={{ background: item.rail }} />
                                <div className={classes.railCardBody}>
                                    <div className={classes.customiseHeader}>
                                        <img src={item.icon} alt="" className={classes.customiseIcon} />
                                        <h3 className={classes.railCardTitle} style={{ fontFamily: pbdqFont }}>{item.title}</h3>
                                    </div>
                                    <p className={classes.railCardDesc} style={{ fontFamily: pbdqFont }}>{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            </div>

            {/* <div className={classes.bandGrey}>
                <div className={clsx(shared.container, classes.gutter)}>
                    <section className={classes.section}>
                        <div className={classes.sectionHead}>
                            <span className={classes.sectionEyebrow}>Two ways to begin</span>
                            <h2 className={classes.sectionTitle}>Start from an identified gap — or from <span className={classes.accent}>your own priority.</span></h2>
                        </div>
                        <div className={classes.twoWaysGrid}>
                            <div className={classes.twoWaysCard}>
                                <span className={classes.twoWaysRail} style={{ background: '#3CC6F3' }} />
                                <h3 className={classes.twoWaysTitle} style={{ fontFamily: pbdqFont }}>Create from assessment evidence</h3>
                                <p className={classes.twoWaysCopy} style={{ fontFamily: pbdqFont }}>Use findings from School Child Online Safety Readiness to identify priority capability gaps and create targeted learning for particular year groups, classes or schools.</p>
                            </div>
                            <div className={classes.twoWaysCard}>
                                <span className={classes.twoWaysRail} style={{ background: '#F8B143' }} />
                                <h3 className={classes.twoWaysTitle} style={{ fontFamily: pbdqFont }}>Create from your own learning need</h3>
                                <p className={classes.twoWaysCopy} style={{ fontFamily: pbdqFont }}>Begin with a topic, curriculum requirement, emerging issue or school priority and develop a program around the outcomes you want students to achieve.</p>
                            </div>
                        </div>
                        <p className={classes.twoWaysNote}>Whether the starting point is assessment evidence or educator judgement, LearnCreator turns the need into structured, measurable learning.</p>
                    </section>
                </div>
            </div>

            <div className={clsx(shared.container, classes.gutter)}>
                <section className={classes.section}>
                    <div className={classes.twoColGrid}>
                        <div className={classes.twoColCopy}>
                            <span className={classes.sectionEyebrow}>School-wide creation</span>
                            <h2 className={classes.sectionTitle}>Equip every educator to <span className={classes.accent}>create and adapt learning.</span></h2>
                            <p className={classes.sectionIntro}>The most effective way to use LearnCreator is to give educators across the school access to the creation process.</p>
                            <p className={classes.twoColPara}>Teachers can develop age-appropriate programs for their subjects and students, while school leaders can maintain visibility and oversight across the school. Programs can be shared, adapted and reused instead of being rebuilt independently.</p>
                            <ul className={classes.checkList}>
                                {SCHOOLWIDE_ITEMS.map((item) => (
                                    <li key={item} className={classes.checkItem}>
                                        <span className={classes.checkIcon}><CheckIcon /></span>
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className={classes.footnote}>Programs can be delivered as standalone courses or embedded within existing subjects and school activities.</p>
                        </div>
                        <PbdqImageLightboxWrapper
                            src={LearnCreatorSchoolwide}
                            alt="Educators creating programs together in LearnCreator"
                            caption="Educators collaborating and managing programs in LearnCreator"
                            className={classes.twoColLightboxWrap}
                        >
                            <img src={LearnCreatorSchoolwide} alt="Educators creating programs together in LearnCreator" className={classes.twoColImage} />
                        </PbdqImageLightboxWrapper>
                    </div>
                </section>
            </div>

            <div className={classes.bandTint}>
                <div className={clsx(shared.container, classes.gutter)}>
                    <section className={classes.section}>
                        <div className={clsx(classes.twoColGrid, classes.twoColGridEven)}>
                            <div className={classes.twoColCopy}>
                                <span className={classes.sectionEyebrow}>Creation capacity</span>
                                <h2 className={classes.sectionTitle}>Use credits wherever <span className={classes.accent}>program creation is needed most.</span></h2>
                                <p className={classes.sectionIntro}>One creation credit supports the creation of approximately one 45-minute learning session.</p>
                                <p className={classes.twoColPara}>Each annual deployment includes one creation credit for every ten learners. Credits are pooled across the deployment rather than assigned to individual educators, allowing schools and networks to direct their capacity where it is most valuable.</p>
                            </div>
                            <div className={classes.exampleCard}>
                                <span className={classes.exampleLabel}>Example</span>
                                <p className={classes.exampleIntro}>An annual deployment for 200 learners includes 20 creation credits. The school could use these credits to create:</p>
                                <ul className={classes.checkList}>
                                    {EXAMPLE_ITEMS.map((item) => (
                                        <li key={item} className={classes.checkItem} style={{ fontSize: 15 }}>
                                            <span className={classes.checkIcon}><CheckIcon color="#F8B143" /></span>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                                <p className={classes.footnote}>Additional creation credits can be purchased when more program-development capacity is required.</p>
                            </div>
                        </div>
                    </section>
                </div>
            </div> */}

            <div className={classes.bandGrey}>
                <div className={clsx(shared.container, classes.gutter)}>
                    <section className={classes.section}>
                        <div className={classes.sectionHead}>
                            <span className={classes.sectionEyebrow}>Why schools choose LearnCreator</span>
                            <h2 className={classes.sectionTitle}>Make tailored program creation practical at <span className={classes.accent}>school scale.</span></h2>
                        </div>
                        <div className={classes.grid3}>
                            {WHY_ITEMS.map((item) => (
                                <div key={item.title} className={classes.railCard}>
                                    <span className={classes.railCardRail} style={{ background: item.rail }} />
                                    <div className={classes.railCardBody}>
                                        <h3 className={classes.railCardTitle} style={{ fontFamily: pbdqFont }}>{item.title}</h3>
                                        <p className={classes.railCardDesc} style={{ fontFamily: pbdqFont }}>{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>
            </div>

            <div className={classes.band}>
                <div className={clsx(shared.container, classes.gutter)}>
                    <section className={classes.section}>
                        <div className={clsx(classes.twoColGrid, classes.twoColGridEven)}>
                            <div className={classes.twoColCopy}>
                                <span className={classes.sectionEyebrow}>Standards and framework alignment</span>
                                <h2 className={classes.sectionTitle}>Create from a globally <span className={classes.accent}>aligned foundation.</span></h2>
                                <p className={classes.sectionIntro}>LearnCreator is grounded in the DQ framework underlying IEEE 3527.1™ and can align programs with your national curriculum and selected international frameworks.</p>
                                <p className={classes.twoColPara}>This enables schools to connect local learning priorities with a consistent structure for developing and measuring AI-digital competencies.</p>
                                <div className={classes.standardsCard}>
                                    <span className={classes.standardsCardLabel}>Alignment options include</span>
                                    <p className={classes.standardsCardText}>IEEE 3527.1™ · national curricula · digital-literacy frameworks · child online safety frameworks · organizational standards · selected reference programs.</p>
                                </div>
                            </div>
                            <PbdqImageLightboxWrapper
                                src={LearnCreatorAlignment}
                                alt="Framework alignment options in LearnCreator"
                                caption="Curriculum and framework alignment options in LearnCreator"
                                className={classes.twoColLightboxWrap}
                            >
                                <img src={LearnCreatorAlignment} alt="Framework alignment options in LearnCreator" className={classes.twoColImage} />
                            </PbdqImageLightboxWrapper>
                        </div>
                    </section>
                </div>
            </div>

            <section className={classes.ctaSection} aria-label="Next steps">
                <div className={classes.ctaContainer}>
                    <div className={classes.ctaTextCol}>
                        <h2 className={classes.ctaTitle}>Turn your next learning need into a measurable program.</h2>
                        <p className={classes.ctaLead}>Create learning tailored to your students, aligned with your curriculum and designed to show what improves.</p>
                    </div>
                    <div className={classes.ctaActions}>
                        <button type="button" onClick={scrollToHowItWorks} className={classes.btnCtaPrimary}>Try LearnCreator</button>
                        <Link to={pbdqRoutes.getStarted} className={classes.btnCtaOutline}>Get started</Link>
                    </div>
                </div>
            </section>

            <PbdqFooter />
        </div>
    );
};

export default PbdqLearnCreatorPageContent;
